import { FC } from "react";
import { Stock } from "@/models/stock";
import { StockShortKeyData } from "@/controllers/stock";
import styles from "./StockKeyData.module.scss";

/**
 * A one-paragraph, server-rendered summary of a stock's key numbers.
 *
 * The overview's widgets all load in the browser, so the HTML a crawler or an
 * AI answer engine fetched used to carry the name, the ticker and nothing else
 * to quote. This states the headline numbers as a sentence, from the page props,
 * so they are in the first response. It is plain visible text on purpose: hidden
 * text written for crawlers is what search engines penalize.
 *
 * Short interest, days to cover and cost to borrow come from
 * getStockShortKeyDataOnServer and are left out when it has nothing; the rest
 * is always there.
 */

const compactUsd = (value: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 2 }).format(value);

const price = (value: number, currency: string) => {
    // GBP stocks are quoted in pence; getStockInfoOnServer has already scaled them.
    if (currency === "GBP") return `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })} GBp`;
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
    } catch {
        return `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${currency}`;
    }
};

const isoDate = (ms: number | null | undefined) => (ms ? new Date(ms).toISOString().slice(0, 10) : null);

const pct = (value: number) => `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

interface Props {
    stock: Stock & { trading_day?: number };
    shortData?: StockShortKeyData | null;
}

const StockKeyData: FC<Props> = ({ stock, shortData }) => {
    const sentences: string[] = [];
    const label = `${stock.name} (${stock.exchangesymbol}: ${stock.ticker})`;

    if (finite(stock.mkt_cap_usd) && stock.mkt_cap_usd > 0) sentences.push(`${label} has a market capitalization of ${compactUsd(stock.mkt_cap_usd)}.`);
    else sentences.push(`${label}.`);

    const asOf = isoDate(stock.trading_day);
    if (finite(stock.last_close) && stock.currency) {
        let text = `Last close ${price(stock.last_close, stock.currency)}${asOf ? ` (as of ${asOf})` : ""}`;
        if (finite(stock.low_52_week) && finite(stock.high_52_week)) text += `; 52-week range ${price(stock.low_52_week, stock.currency)} to ${price(stock.high_52_week, stock.currency)}`;
        sentences.push(`${text}.`);
    }

    if (shortData) {
        const parts: string[] = [];
        if (finite(shortData.siPercentFreeFloat)) parts.push(`short interest is ${pct(shortData.siPercentFreeFloat)} of free float`);
        if (finite(shortData.daysToCover)) parts.push(`${shortData.daysToCover.toLocaleString("en-US", { maximumFractionDigits: 1 })} days to cover`);
        if (finite(shortData.costToBorrow)) parts.push(`the average cost to borrow is ${pct(shortData.costToBorrow)}`);
        if (parts.length) {
            const list = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0];
            const text = `ORTEX data${shortData.asOf ? ` as of ${shortData.asOf}` : ""}: ${list}.`;
            sentences.push(text);
        }
    }

    return (
        <section className={styles.StockKeyData} aria-labelledby="stock-key-data">
            <h2 id="stock-key-data">{`${stock.ticker} key data`}</h2>
            <p>{sentences.join(" ")}</p>
        </section>
    );
};

export default StockKeyData;
